import type { DropdownMenuItem } from '@timelyapp/react';
import { Button, DropdownMenu } from '@timelyapp/react';
import classNames from 'classnames';
import { Ellipsis } from 'components/Icons';
import type { Option } from 'components/popovers/PopoverDropdownList';
import React, { useMemo } from 'react';
import FontAwesome from 'react-fontawesome';
import styles from './index.module.scss';
import type { TdProps } from './Td';
import { Td } from './Td';

interface TdActionsProps<T> extends Omit<TdProps, 'onClick'> {
  onClick: (t: T) => void;
  options: Array<T>;
  /** Prevent event bubbling, useful when <Tr> parent has an onClick listener. */
  stopPropagation?: boolean;
  /** Control dropdown state externally */
  isOpen?: boolean;
  /** Callback when dropdown is closed */
  onClose?: () => void;
  /** Called when dropdown opens */
  onOpen?: () => void;
}

/**
 * Translate legacy DropdownOption to new DropdownMenuItem format
 */
function translateOptionsToDropdownItems<T extends Option>(
  options: Array<T>,
  onClick: (option: T) => void
): DropdownMenuItem[] {
  return options.map((option, index) => {
    if (option.separator) {
      return { type: 'separator' as const };
    }

    // Determine icon: prefer custom icon, then faIcon
    let icon: React.ReactElement | undefined;
    if (option.icon) {
      icon = option.icon;
    } else if (option.faIcon) {
      icon = <FontAwesome name={option.faIcon} />;
    }

    const item: DropdownMenuItem = {
      type: 'item' as const,
      key: option.action || `option-${index}`,
      label: option.name || '',
      icon,
      disabled: option.disabled,
      onSelect: () => onClick(option),
    };

    // Map variant based on action name conventions
    if (option.action === 'delete' || option.type === 'danger') {
      item.variant = 'danger';
    }

    // Map confirm property for destructive actions
    if (option.confirm) {
      item.confirm = {
        prompt: option.confirmMessage || 'Are you sure?',
        confirmLabel: option.action === 'delete' ? 'Delete' : 'Confirm',
        confirmButtonDestructive: option.action === 'delete' || option.type === 'danger',
      };
    }

    return item;
  });
}

export const TdActions = <T extends Option>(props: TdActionsProps<T>) => {
  const {
    className,
    onClick,
    stopPropagation = true,
    isOpen,
    onOpen,
    onClose,
    options,
    ...rest
  } = props;
  const containerClass = classNames(styles.tdActions, className);

  const dropdownItems = useMemo(
    () => translateOptionsToDropdownItems(options, onClick),
    [options, onClick]
  );

  function handleContainerClick(event: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>) {
    stopPropagation && event.stopPropagation();
  }

  return (
    <Td {...rest} className={containerClass} rightAligned onClick={handleContainerClick}>
      <div className={styles.trigger} data-testid="edit-index">
        <DropdownMenu
          open={isOpen}
          onOpenChange={open => {
            if (open) {
              onOpen?.();
            } else {
              onClose?.();
            }
          }}
          closeOnSelect
          trigger={
            <Button disabled={!options.length} size="small" priority="low">
              <Ellipsis />
            </Button>
          }
          items={dropdownItems}
        />
      </div>
    </Td>
  );
};
